import React, { memo, useMemo, useState } from "react"
import { Grid, Paper, Button } from "@material-ui/core"
import ListableRulesCard from "components/listable-rules-card/ListableRulesCard"
// import { Button } from "components/button/Button"
import { useTranslation } from "react-i18next"
import { useSelector, useDispatch } from "react-redux"
import { RootState } from "state/reducers/AppReducer"
import { FixMeLater } from "types/FixMeLaterType"
import { updateLocalTargetGroup } from "state/actions/TargetGroupActions"
import { createTargetFields } from "state/constants/TargetFields"
import { RuleType, ValueType } from "state/constants/RuleType"
import { ListableRulesCardDataModel } from "components/listable-rules-card/listable-item-rules/ListableRulesItem"
import { Option } from "components/textbox/Textbox"

interface Props {
    // Fields
    isEditing: boolean,
    modelType: string
}

interface TargetGroup {
    id?: string,
    DisplayName: string,
    ModelType: string,
    TokenField: string,
    SalesOrganisationIds: string,
    ContainsAnyToken: string[]
    FilterRules: TargetGroupRule[]
    CallType?: string
}

interface TargetGroupRule {
    Field?: string | null,
    FilterType?: string | null,
    Eq?: boolean | null,
    Matching?: string | null,
    MatchingOneOf?: string[] | null
}

function VariableRuleManager(props: Props) {
    // Selectors
    const currentTargetGroup = useSelector<RootState, FixMeLater>(state => state.TargetGroupReducer.currentTargetGroup)
    const codedValues = useSelector<RootState, FixMeLater>(state => state.CodedValueReducer.codedValues)

    // Utility
    const [t, i18n] = useTranslation()
    const dispatch = useDispatch()

    // Meta-State
    const [snackbarOpen, setSnackbarOpen] = useState(false)
    const [snackbarMessage, setSnackbarMessage] = useState("")


    const ruleFields = useMemo(() => createTargetFields()[props.modelType].RULE, [t, createTargetFields, props.modelType])

    /*
        Function for populating options dropdowns from coded values
    */
    const generateOptions = (rule: FixMeLater): Option[] => {
        const isOptionsType = rule.FilterType === RuleType.Equal.key || rule.FilterType === RuleType.Contains.key || rule.FilterType === RuleType.TitleOrUpperCase.key
        var data: Option[] = []
        if (rule.Field != null && (isOptionsType)) {
            const field = ruleFields[rule.Field]
            data = Object.keys(codedValues).filter(codedValue => field != null && field.CodeType === codedValues[codedValue].CodeType).map((selected): Option => {
                const codedValue = codedValues[selected]
                return { title: codedValue.CodeValue, inputValue: codedValue.CodeId }
            })
        }

        return data
    }

    /*
        Function for populating the value of a rule
     */
    const generateValue = (rule: FixMeLater): string[] | boolean | undefined => {

        return rule.FilterType === RuleType.Bool.key
            ? rule["Eq"]
            : rule[ValueType.MatchingOneOf.key] !== undefined
                ? rule[ValueType.MatchingOneOf.key]
                : rule[ValueType.Matching.key] != undefined
                    ? [rule[ValueType.Matching.key]]
                    : undefined
    }

    /*
        Function for setting up the possible fields and options for each rule.
    */
    function setupRulesData(): ListableRulesCardDataModel[] {
        if (currentTargetGroup?.FilterRules == null) {
            return []
        }

        const modelType: string = currentTargetGroup.ModelType.toUpperCase()

        const ruleData = currentTargetGroup.FilterRules.map((rule: FixMeLater, index: number): ListableRulesCardDataModel => {
            return {
                id: index.toString(), // TODO: Fix?
                fieldNameValue: rule.Field,
                handleFieldNameChange: (item: { key: string, value: string }) => {
                    if (item?.key != null) {
                        const value = item.key
                        var targetGroup: TargetGroup = Object.assign({}, currentTargetGroup);
                        targetGroup.FilterRules[index].Field = value;
                        targetGroup.FilterRules[index].FilterType = createTargetFields()[modelType].RULE[value].FilterType
                        dispatch(updateLocalTargetGroup(targetGroup, () => {

                        }))
                    } else {
                        var targetGroup: TargetGroup = Object.assign({}, currentTargetGroup);
                        targetGroup.FilterRules[index].Field = undefined;
                        targetGroup.FilterRules[index].FilterType = undefined
                        targetGroup.FilterRules[index].Eq = undefined
                        targetGroup.FilterRules[index].Matching = undefined
                        targetGroup.FilterRules[index].MatchingOneOf = undefined
                        dispatch(updateLocalTargetGroup(targetGroup, () => {

                        }))
                    }

                },
                value: generateValue(rule),
                options: generateOptions(rule),
                handleValueChange: (value) => {
                    var targetGroup = Object.assign({}, currentTargetGroup);

                    if (Array.isArray(value) && value.length === 0) {
                        // Reject early if its a "non-value".
                        targetGroup.FilterRules[index].Matching = undefined
                        targetGroup.FilterRules[index].MatchingOneOf = undefined;
                    } else if (rule.FilterType === RuleType.Bool.key) {
                        // If value is bool
                        targetGroup.FilterRules[index].Eq = value;
                        targetGroup.FilterRules[index].Matching = undefined;
                        targetGroup.FilterRules[index].MatchingOneOf = undefined;
                    } else if (Array.isArray(value) && value.length > 1) { //typeof value == "string" && value.indexOf(',') > -1 && value.split(",").length > 1) {
                        // If Value is string[]
                        const matchingOneOfList = value
                        targetGroup.FilterRules[index].MatchingOneOf = matchingOneOfList
                        targetGroup.FilterRules[index].Eq = undefined;
                        targetGroup.FilterRules[index].Matching = undefined;
                    } else if (Array.isArray(value) && value.length == 1) {
                        // If value is string
                        targetGroup.FilterRules[index].Eq = undefined;
                        targetGroup.FilterRules[index].Matching = value[0];
                        targetGroup.FilterRules[index].MatchingOneOf = undefined;
                    }
                    dispatch(updateLocalTargetGroup(targetGroup, () => {

                    }))
                }
            }
        })

        return ruleData
    }

    /*
        Delete rules from target group
        @param data List of rules to delete
    */
    const handleDeleteRule = (data: string[]) => {
        var targetGroup = Object.assign({}, currentTargetGroup);
        targetGroup.FilterRules = targetGroup.FilterRules.filter((_: string, index: number) => !data.includes(`${index}`));
        dispatch(updateLocalTargetGroup(targetGroup, () => {

        }))
    }

    /*
        Handle adding a rule to a target group
    */
    const handleAddRule = () => {
        if (!currentTargetGroup.ModelType) {
            setSnackbarOpen(true)
            setSnackbarMessage(t('target-group.model-type-rule'))
        } else {
            var targetGroup = Object.assign({}, currentTargetGroup);
            targetGroup.FilterRules = targetGroup.FilterRules == null ? [] : targetGroup.FilterRules;

            targetGroup.FilterRules.push({
                Field: undefined,
                FilterType: undefined,
                MatchingOneOf: undefined,
                Matching: undefined,
                Eq: undefined,
            });

            dispatch(updateLocalTargetGroup(targetGroup, () => {

            }))
        }
    }

    return (
        <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <Paper>
                <ListableRulesCard
                    actionButton={[
                        <Button
                            onClick={handleAddRule}
                            variant="outlined"
                            color="secondary"
                        >
                            {t('target-group.add-rule')}
                        </Button>
                    ]}
                    addLabel={t('target-group.add-rule')}
                    disabled={!props.isEditing}
                    isLoading={false}
                    title={t('target-group.rules')}
                    emptyPlaceholder={t('target-group.no-rules')}
                    handleDelete={handleDeleteRule}
                    data={setupRulesData() == null ? [] : setupRulesData()}
                    modelType={props.modelType.toUpperCase()}
                    subheading={t('target-group.rules-helper')}
                />
            </Paper>
        </Grid>
    )
}

export default memo(VariableRuleManager)